import {MultilangText} from "../api/Ticketshop";
import {SessionService} from '../services/session.service';

export interface I18nData {
    [key: string]: I18nEntry;
}

export interface I18nEntry {
    [lang: string]: string;
}

export class I18n {

	private static instance: I18n;

	private lang: string;

    private readonly fallbackLocales: string[] = ['en', 'de'];

	constructor(lang?: string) {
		this.lang = lang

		if (!this.lang) {
			this.lang = document.querySelector('html').lang;
		}

		if (!this.lang) {
			this.lang = 'de';
		}
	}

	static getInstance() {
		if (!I18n.instance) {
			I18n.instance = new I18n();
		}

		return I18n.instance;
	}

	setLang(lang: string) {
		this.lang = lang;
	}

	getLang():string {
		return this.lang;
	}

    public getMessage(name: string, defaultValue: string = '', values?: string[], messageObject?: MultilangText & any, keepHtml = false): string {
        // Get message string with fallbacks
        let output = this.getMessageString(name, defaultValue, messageObject);

        if (values && values.length) {
            values.forEach((value, i) => {
                output = output.replace(`{${i}}`, value);
            });
        }

        return keepHtml ? output : htmlDecode(output);
    }

    private getMessageString(name: string, defaultValue: string = '', messageObject?: MultilangText): string {
        const primaryMessage = this.getMessageForLocale(this.lang, name, messageObject);
        if (primaryMessage) {
            // Message exists in primary language, just use it
            return primaryMessage;
        }

        if (SessionService.instance.isDebug()) {
            // Show warning, if debug mode is active
            return `[${name} übersetzen]`;
        }

        for (const lang of this.fallbackLocales) {
            // Try fallback languages in order
            const fallbackMessage = this.getMessageForLocale(lang, name, messageObject);
            if (fallbackMessage) {
                return fallbackMessage;
            }
        }

        if (defaultValue) {
            // Use default value as fallback
            return defaultValue;
        }

        // Nothing found, use empty string
        return '';
    }

    private getMessageForLocale(locale: string, name: string, messageObject?: MultilangText): string | null {
        // Try to find message in the i18n strings
        const messagePart = SessionService.instance.getI18nData()?.[name];
        if (messagePart?.[locale]) {
            return messagePart[locale];
        }

        // Use the message from the MultilangText object
        if (messageObject?.[locale] && messageObject[locale] !== '.') {
            return messageObject[locale];
        }

        // Nothing found in the specified locale
        return null;
    }

    public getMessageArray(prefix: string): string[] {
        const result = [];
        for (let i = 0; ; i++) {
            const name = `${prefix}.${i}`;
            const message = this.getMessage(name);

            if (!message) {
                // Empty result: We are done
                break;
            }
            if (SessionService.instance.isDebug() && i > 0 && message === `[${name} übersetzen]`) {
                // Debug mode: Make sure we don't end up in an endless loop
                break;
            }

            result.push(message);
        }

        return result;
    }
}

export function findLocale(element: HTMLElement): string {
	const closestElement = element.closest('[lang]') as HTMLElement;
	if (closestElement) {
		return closestElement.lang;
	}
	if (document.body.lang) {
		return document.body.lang;
	}
	if (document.documentElement.lang) {
		return document.documentElement.lang;
	}

	return 'de';
	// return "en";
}

const i18n = I18n.getInstance();

const COUNTRY_NAMES = new Map(
    [
        [ "Österreich", "reduction-cards.country-names.austria" ],
        [ "Deutschland", "reduction-cards.country-names.germany" ],
        [ "Italien", "reduction-cards.country-names.italy" ],
        [ "Schweiz", "reduction-cards.country-names.switzerland" ],
        [ "Slowakei", "reduction-cards.country-names.slovakia" ],
        [ "International", "reduction-cards.country-names.international" ],
        [ "Dänemark", "reduction-cards.country-names.denmark" ],
        [ "Kroatien", "reduction-cards.country-names.croatia" ],
        [ "Slowenien", "reduction-cards.country-names.slovenia" ],
        [ "Tschechien", "reduction-cards.country-names.czechia" ],
        [ "Ungarn", "reduction-cards.country-names.hungary" ],
    ]
)

export function translateCountryName(countryName: string): string {
    if (COUNTRY_NAMES.has(countryName)) {
        return COUNTRY_NAMES.get(countryName);
    } else {
        return "reduction-cards.country-names.default";
    }
}

export { i18n };

const domParser = new DOMParser();
export function htmlDecode(input: string) {
    const element = domParser.parseFromString(input, "text/html");
    return element.documentElement.textContent;
}
